import { apiClient } from '../client';
import { API_CONFIG } from '../config';

/**
 * Resolves a full URL to a template asset on the WB S3 bucket. Both the
 * user-side gallery and the super-admin panel call into this helper so
 * the two surfaces NEVER drift — replacing the thumbnail in S3 updates
 * both immediately.
 *
 * Resolution order:
 *   1. `VITE_CDN_URL` if set — production CloudFront fronting wenext-websites
 *   2. Backend asset proxy `{VITE_API_BASE_URL}/wb/template-asset/` — works
 *      in dev (private S3 bucket) and prod (no CDN needed). The backend's
 *      unauthenticated `/wb/template-asset/*` route streams the S3 object
 *      with a strict `templates/v1/**` scope guard.
 *
 * The catalog field `template.thumbnail` (user gallery) and admin row
 * `thumbnail_s3_key` (admin panel) both hold the SAME relative S3 key
 * (e.g. `templates/v1/saasland-1/thumb.jpg`), so concatenating against
 * either base URL produces the right CDN/proxy URL.
 *
 * @param s3Key relative S3 key, e.g. `templates/v1/saasland-1/thumb.jpg`.
 *              Falsy / empty input returns an empty string so callers can
 *              guard with `if (url)` and fall back to a placeholder.
 */
export function resolveTemplateThumbnailUrl(s3Key: string | null | undefined): string {
  if (!s3Key) return '';
  const env = (import.meta as any).env ?? {};
  if (env.VITE_CDN_URL) {
    return `${String(env.VITE_CDN_URL).replace(/\/+$/, '')}/${s3Key}`;
  }
  const apiBase = (env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
  return `${apiBase}/wb/template-asset/${s3Key}`;
}

export interface WbSite {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  subdomain?: string;
  domain?: string;
  description?: string;
  logo_url?: string;
  favicon_url?: string;
  theme?: Record<string, any>;
  settings?: Record<string, any>;
  is_published: boolean;
  published_at?: string;
  created_at: string;
  updated_at: string;
}

export interface WbPage {
  id: string;
  organization_id: string;
  site_id: string;
  title: string;
  slug: string;
  mode?: 'static' | 'ai-code' | 'collection-list' | 'collection-detail' | 'form';
  content?: Record<string, any>;
  published_content?: Record<string, any>;
  // ai-code
  page_code?: string;
  compiled_bundle?: string;
  compiled_at?: string;
  // dynamic
  data_source_id?: string;
  route_pattern?: string;
  route_param_field?: string;
  page_schema?: Record<string, any>;
  // seo
  meta_title?: string;
  meta_description?: string;
  og_image?: string;
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const wbSitesApi = {
  list: () =>
    apiClient.get<WbSite[]>('/wb/sites'),

  get: (siteId: string) =>
    apiClient.get<WbSite>(`/wb/sites/${siteId}`),

  create: (payload: { name: string; slug: string; domain?: string; description?: string }) =>
    apiClient.post<WbSite>('/wb/sites', payload),

  update: (siteId: string, payload: Partial<WbSite>) =>
    apiClient.put<WbSite>(`/wb/sites/${siteId}`, payload),

  delete: (siteId: string) =>
    apiClient.delete<void>(`/wb/sites/${siteId}`),
};

export const wbAiApi = {
  generate: (prompt: string) =>
    apiClient.post<{ html: string }>('/wb/ai-generate', { prompt }),

  generateStream: async (
    prompt: string,
    history: Array<{ role: 'user' | 'assistant'; content: string; isHtml?: boolean }>,
    onChunk: (chunk: string) => void,
    onDone: () => void,
    onError: (msg: string) => void,
  ) => {
    const token = localStorage.getItem('auth_token');
    // Use VITE_API_BASE_URL (the codebase-wide convention) — falling back to
    // a relative path proxied by Vite to :3001. Earlier this read VITE_API_URL
    // (a typo), which silently dropped any user override and forced every
    // streaming WB call through the Vite proxy. When the user's backend ran
    // on a different port (e.g. :4004), the proxy returned HTTP 500 with
    // ECONNREFUSED — surfaced as the now-fixed "HTTP 500 — see browser
    // console" error on the gallery instantiate flow. Regression-locked
    // via the env-var consistency check in the websiteBuilder service test.
    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1';
    const res = await fetch(`${baseUrl}/wb/ai-generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ prompt, history }),
    });

    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => 'Unknown error');
      onError(text);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') { onDone(); return; }
        try {
          const parsed = JSON.parse(data);
          if (parsed.error) { onError(parsed.error); return; }
          if (parsed.content) onChunk(parsed.content);
        } catch { /* ignore malformed lines */ }
      }
    }
    onDone();
  },

  generateReactStream: async (
    prompt: string,
    history: Array<{ role: 'user' | 'assistant'; content: string }>,
    siteId: string,
    onChunk: (chunk: string) => void,
    onDone: () => void,
    onError: (msg: string) => void,
  ) => {
    const token = localStorage.getItem('auth_token');
    // Use VITE_API_BASE_URL (the codebase-wide convention) — falling back to
    // a relative path proxied by Vite to :3001. Earlier this read VITE_API_URL
    // (a typo), which silently dropped any user override and forced every
    // streaming WB call through the Vite proxy. When the user's backend ran
    // on a different port (e.g. :4004), the proxy returned HTTP 500 with
    // ECONNREFUSED — surfaced as the now-fixed "HTTP 500 — see browser
    // console" error on the gallery instantiate flow. Regression-locked
    // via the env-var consistency check in the websiteBuilder service test.
    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1';
    const res = await fetch(`${baseUrl}/wb/ai-generate-react`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ prompt, history, siteId }),
    });

    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => 'Unknown error');
      onError(text);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') { onDone(); return; }
        try {
          const parsed = JSON.parse(data);
          if (parsed.error) { onError(parsed.error); return; }
          if (parsed.content) onChunk(parsed.content);
        } catch { /* ignore malformed lines */ }
      }
    }
    onDone();
  },

  /**
   * Classify a chat prompt as either single-file edit or multi-page creation.
   * The unified AI chat calls this BEFORE deciding which pipeline to run.
   *
   * Response is always shape-stable — any backend failure surfaces as
   * `{ intent: "edit", confidence: 0, reason }`. Callers can therefore
   * always proceed; the worst case is the user re-prompts if the routing
   * was off.
   *
   * Cost: ~$0.00005 per call (gpt-4o-mini, 200 input + 50 output tokens).
   * Adds ~500ms to every chat message — acceptable vs the 15-60s
   * generation time it gates.
   */
  classifyIntent: (body: {
    prompt: string;
    activeFile?: string;
    siteHasPages?: boolean;
  }) =>
    apiClient.post<{
      intent: 'edit' | 'multi_page';
      confidence: number;
      page_hints?: string[];
      reason?: string;
    }>('/wb/ai-classify-intent', body),
};

// ---------------------------------------------------------------------------
// WB UI component runtime — the prebuilt component globals (Button / Card /
// FeatureCard / Form / FormField) the editor preview inlines into the iframe
// so preview === the server-inlined published page. The endpoint returns raw
// JS (NOT a JSON envelope), so we use a raw fetch returning text instead of
// apiClient. Never throws — returns '' on any failure so the preview still
// renders raw-markup pages (only component pages degrade in preview;
// published is unaffected since it inlines the runtime server-side).
// ---------------------------------------------------------------------------

export const wbRuntimeApi = {
  getUi: async (): Promise<string> => {
    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1';
    try {
      const res = await fetch(`${baseUrl}/wb/runtime/wb-ui.js`, { method: 'GET' });
      if (!res.ok) return '';
      return await res.text();
    } catch {
      return '';
    }
  },
};

// ---------------------------------------------------------------------------
// Multi-page AI generation — async job + SSE subscriber pattern.
//
// Two-step flow (decouples generation lifetime from any single HTTP request):
//   1. POST  /wb/ai-generate-job              → { jobId } in <300ms
//   2. GET   /wb/ai-generate-job/:jobId/stream → SSE of progress events
//   (3.) DELETE /wb/ai-generate-job/:jobId    → cancel + rollback partial pages
//
// Event shape on the SSE stream (each as a JSON object, terminated by `[DONE]`):
//   { phase: "queued"|"planning"|"planned"|"datastore_setup"|"datastore_ready"
//          |"page_generating"|"page_saved"|"finalizing"|"completed"
//          |"failed"|"refused"|"cancelled", ...payload }
//
// EventSource auto-reconnects on transient drops. The backend replays the
// last-known phase from BullMQ's stored `job.progress` on each fresh subscribe,
// so reconnection picks up coherent state without restarting generation.
// ---------------------------------------------------------------------------

export interface WbAiJobStartBody {
  siteId: string;
  prompt: string;
}

export interface WbAiJobStartResponse {
  jobId: string;
  siteId: string;
  status: 'queued';
}

/** Discriminated union of every SSE event the server can emit. */
export type WbAiJobEvent =
  | { phase: 'queued'; site_id?: string; ts?: number }
  | { phase: 'planning'; ts?: number }
  | {
      phase: 'planned';
      pages?: Array<{ slug: string; title: string; role: string; is_home?: boolean }>;
      datastore_required?: boolean;
      ts?: number;
    }
  | { phase: 'datastore_setup'; kind?: string; name?: string; ts?: number }
  | { phase: 'datastore_ready'; ts?: number }
  | {
      phase: 'page_generating';
      page_slug: string;
      page_title?: string;
      page_role?: string;
      page_index: number;
      page_total: number;
      ts?: number;
    }
  | {
      phase: 'page_saved';
      page_slug: string;
      page_id: string;
      page_role?: string;
      page_index: number;
      page_total: number;
      is_home?: boolean;
      ts?: number;
    }
  | { phase: 'finalizing'; ts?: number }
  | {
      phase: 'completed';
      status?: 'completed';
      site_id: string;
      page_count: number;
      page_slugs: string[];
      pages?: Array<{ slug: string; page_id: string }>;
      datastore_created?: boolean;
      datastore_id?: string | null;
      first_page_slug?: string | null;
      first_page_id?: string | null;
      ts?: number;
    }
  | {
      phase: 'failed';
      reason: string;
      message?: string;
      page?: string;
      ts?: number;
    }
  | { phase: 'refused'; reason: string; message?: string; ts?: number }
  | { phase: 'cancelled'; ts?: number };

/**
 * Returned by `wbAiJobApi.subscribe()` — gives the caller a way to detach
 * the SSE listener without referencing the underlying EventSource directly
 * (so the implementation can swap transports later without breaking callers).
 */
export interface WbAiJobSubscription {
  /** Close the SSE channel + remove all listeners. Idempotent. */
  close(): void;
  /** True after close() has been called. */
  readonly closed: boolean;
}

export const wbAiJobApi = {
  /**
   * Step 1 — kick off a multi-page generation job. Returns immediately with
   * a job ID; the actual generation runs in a BullMQ worker decoupled from
   * this HTTP request.
   *
   * Surfaces every failure via the apiClient swallow-error pattern so the
   * UI can branch on `res.ok` instead of try/catch (consistent with the
   * rest of websiteBuilder.ts; see the "apiClient swallows" gotcha in
   * CLAUDE.md's "Audit Logging" and "Per-Integration Permissions" sections).
   */
  start: (body: WbAiJobStartBody) =>
    apiClient.post<WbAiJobStartResponse>('/wb/ai-generate-job', body),

  /**
   * Step 2 — subscribe to the SSE progress stream for a job. The returned
   * handle lets callers close the connection without holding a reference
   * to the raw EventSource.
   *
   * Reconnection: EventSource auto-reconnects on transient drops. The
   * backend's stream endpoint replays `job.progress` (BullMQ stores only
   * the LATEST progress value) on each fresh subscribe, so a reconnect
   * lands on a coherent phase. If you need exact event replay across
   * disconnects, the caller should buffer events client-side keyed by
   * `phase` (the backend is intentionally simple here).
   *
   * `onEvent` receives every well-formed JSON event. `onDone` fires on the
   * `[DONE]` sentinel (either after a `completed`/`failed`/`refused`/
   * `cancelled` phase emission, OR on graceful close). `onError` fires
   * for non-200 establishment, malformed payloads, or unrecoverable
   * EventSource errors. After onDone or onError fire, the subscription
   * is implicitly closed; calling .close() again is a no-op.
   */
  subscribe(
    jobId: string,
    callbacks: {
      onEvent: (event: WbAiJobEvent) => void;
      onDone: () => void;
      onError: (message: string) => void;
    },
  ): WbAiJobSubscription {
    // We use fetch + manual SSE parsing instead of native EventSource for
    // two reasons:
    //   1. EventSource can't send custom headers, which means the auth
    //      token would have to go in the URL — leaking it into server
    //      access logs and browser history. The Authorization header
    //      keeps it where it belongs.
    //   2. fetch + ReadableStream is the exact same pattern as
    //      `wbTemplatesApi.instantiateStream` in this same file — one
    //      pattern across all WB SSE consumers, one place to harden if
    //      e.g. reconnect logic ever needs to change.
    // Trade-off vs native EventSource: no browser-side auto-reconnect.
    // We handle reconnection at the caller (the modal component) by
    // calling subscribe() again with the same jobId — backend replays the
    // last-known phase from job.progress so re-subscribing is safe.
    const env = (import.meta as any).env ?? {};
    const baseUrl = (env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
    const token = localStorage.getItem('auth_token') || '';
    const url = `${baseUrl}/wb/ai-generate-job/${encodeURIComponent(jobId)}/stream`;

    let closed = false;
    const abortCtl = new AbortController();
    const handle: WbAiJobSubscription = {
      close() {
        if (closed) return;
        closed = true;
        try {
          abortCtl.abort();
        } catch {
          /* ignore */
        }
      },
      get closed() {
        return closed;
      },
    };

    (async () => {
      let res: Response;
      try {
        res = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: abortCtl.signal,
        });
      } catch (err) {
        if (closed) return;
        const msg =
          err instanceof Error && err.name === 'AbortError'
            ? 'Subscription cancelled'
            : err instanceof Error
              ? err.message
              : 'Network error';
        callbacks.onError(msg);
        handle.close();
        return;
      }

      if (!res.ok || !res.body) {
        if (closed) return;
        // Same error-extraction pattern as instantiateStream in this file —
        // surface backend's actual JSON error envelope when present.
        let detail = '';
        try {
          const raw = await res.text();
          if (raw) {
            try {
              const j = JSON.parse(raw);
              detail = j?.error?.message || j?.error || j?.message || raw;
            } catch {
              detail = raw;
            }
          }
        } catch {
          detail = '';
        }
        // eslint-disable-next-line no-console
        console.error('[wbAiJobApi.subscribe] non-OK response', { status: res.status, detail });
        callbacks.onError(detail || `HTTP ${res.status}`);
        handle.close();
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      try {
        // Standard SSE parse: split on newlines, lines starting with `data: `
        // are the payload. Empty line separates events.
        while (!closed) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          // The last segment may be incomplete — keep it in buf for the
          // next read iteration.
          buf = lines.pop() ?? '';
          for (const line of lines) {
            if (closed) break;
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();
            if (data === '[DONE]') {
              callbacks.onDone();
              handle.close();
              return;
            }
            try {
              const parsed = JSON.parse(data) as WbAiJobEvent;
              callbacks.onEvent(parsed);
            } catch (err) {
              // Malformed chunk — log + continue. A single bad event
              // shouldn't poison the whole stream.
              // eslint-disable-next-line no-console
              console.warn('[wbAiJobApi.subscribe] malformed SSE chunk', { data, err });
            }
          }
        }
        if (!closed) {
          // Stream ended without [DONE]. Treat as normal completion so
          // callers aren't left hanging — the backend-side terminal
          // sentinel may have been swallowed by a proxy. The worker
          // emits `phase: "completed"` BEFORE [DONE] regardless, so
          // callers should have already received the terminal state.
          callbacks.onDone();
          handle.close();
        }
      } catch (err) {
        if (closed) return;
        const msg =
          err instanceof Error && err.name === 'AbortError'
            ? 'Subscription cancelled'
            : err instanceof Error
              ? err.message
              : 'Stream read error';
        callbacks.onError(msg);
        handle.close();
      }
    })();

    return handle;
  },

  /**
   * Step 3 (optional) — cancel a running job. The server rolls back any
   * wb_pages rows + S3 objects this job created before responding. Safe
   * to call on an already-terminal job (returns 200 with no-op).
   */
  cancel: (jobId: string) =>
    apiClient.delete<{ success: true; cancelled: boolean }>(
      `/wb/ai-generate-job/${encodeURIComponent(jobId)}`,
    ),
};

// ---------------------------------------------------------------------------
// Templates — single-page website templates instantiated from the S3 catalog.
// Spec: docs/superpowers/specs/2026-05-26-website-builder-templates-design.md
// ---------------------------------------------------------------------------

export interface WbTemplateCatalogEntry {
  id: string;
  name: string;
  category: string;
  business_types: string[];
  tagline: string;
  thumbnail: string;           // S3 key, not full URL — caller resolves
  page_count: number;
  features: string[];
  version: number;
}

export interface WbBusinessInfo {
  business_name?: string;
  business_type?: string;
  services?: string;
  support_phone?: string;
  support_email?: string;
  instagram_handle?: string;
  website?: string;
}

export interface WbInstantiateBody {
  siteName?: string;
  siteSlug?: string;
  businessInfo?: WbBusinessInfo;
  addToSiteId?: string;
}

// SSE event sequence per spec §6. Discriminated on `type` so callers get
// proper narrowing in the onEvent handler.
export type WbInstantiateEvent =
  | { type: 'fetching_template' }
  | { type: 'merging_ai_knowledge' }
  | { type: 'creating_site'; site: { id: string; slug: string; name?: string } }
  | { type: 'personalizing_page'; page_slug: string }
  | { type: 'saved_page'; page_slug: string; page_id: string; ai_status: string }
  | { type: 'done'; site_id: string; first_page_id: string; first_page_slug: string }
  | { type: 'error'; code: string; message: string; missing_fields?: string[] };

export const wbTemplatesApi = {
  list: () =>
    apiClient.get<WbTemplateCatalogEntry[]>('/wb/templates'),

  /**
   * Open an SSE stream against POST /wb/templates/:templateId/instantiate.
   *
   * Contract:
   *   - NEVER throws — every failure (HTTP !ok, empty body, network reject,
   *     server-emitted `error` event) is funneled through onError, then the
   *     helper returns.
   *   - onEvent fires for every well-formed event line; onDone fires once on
   *     the [DONE] sentinel iff no error was surfaced.
   *   - Malformed `data: ...` lines are silently ignored to keep partial
   *     stream corruption from killing the whole instantiate.
   */
  instantiateStream: async (
    templateId: string,
    body: WbInstantiateBody,
    onEvent: (event: WbInstantiateEvent) => void,
    onDone: () => void,
    onError: (msg: string) => void,
  ) => {
    const token = localStorage.getItem('auth_token');
    // Use VITE_API_BASE_URL (the codebase-wide convention) — falling back to
    // a relative path proxied by Vite to :3001. Earlier this read VITE_API_URL
    // (a typo), which silently dropped any user override and forced every
    // streaming WB call through the Vite proxy. When the user's backend ran
    // on a different port (e.g. :4004), the proxy returned HTTP 500 with
    // ECONNREFUSED — surfaced as the now-fixed "HTTP 500 — see browser
    // console" error on the gallery instantiate flow. Regression-locked
    // via the env-var consistency check in the websiteBuilder service test.
    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1';

    let res: Response;
    try {
      res = await fetch(`${baseUrl}/wb/templates/${templateId}/instantiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err) || 'Network error');
      return;
    }

    if (!res.ok || !res.body) {
      // Pull the response body even if it's empty / not-JSON, so the user
      // sees the actual error message instead of just "HTTP 500".
      // Tries JSON first (typical Fastify error envelope), falls back to
      // plain text, then to the bare status code. Also dumps the response
      // to the console so devs can debug without re-running.
      let detail = '';
      try {
        const raw = await res.text();
        if (raw) {
          try {
            const j = JSON.parse(raw);
            detail = j?.error?.message || j?.error || j?.message || raw;
          } catch {
            detail = raw;
          }
        }
      } catch {
        detail = '';
      }
      // eslint-disable-next-line no-console
      console.error('[wb.instantiateStream] non-OK response', { status: res.status, detail });
      onError(detail || `HTTP ${res.status} — see browser console for response body`);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') { onDone(); return; }
          try {
            const parsed = JSON.parse(data) as WbInstantiateEvent;
            // Server-emitted error event — surface via onError and stop the
            // stream. Do NOT fire onDone on the trailing [DONE] in this
            // branch (spec §6: error path is { error event, [DONE] } but
            // callers expect terminal-onError semantics).
            if (parsed && parsed.type === 'error') {
              onError(parsed.message || parsed.code || 'Instantiate failed');
              return;
            }
            onEvent(parsed);
          } catch { /* ignore malformed lines */ }
        }
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err) || 'Stream read error');
      return;
    }
    // Stream closed without [DONE] — treat as normal completion so callers
    // aren't left hanging. (Server-side watchdog rollback will have emitted
    // an error event before closing in the failure case.)
    onDone();
  },
};

export const wbPagesApi = {
  list: (siteId: string) =>
    apiClient.get<WbPage[]>(`/wb/sites/${siteId}/pages`),

  get: (siteId: string, pageId: string) =>
    apiClient.get<WbPage>(`/wb/sites/${siteId}/pages/${pageId}`),

  create: (siteId: string, payload: { title: string; slug: string; mode?: string }) =>
    apiClient.post<WbPage>(`/wb/sites/${siteId}/pages`, payload),

  update: (siteId: string, pageId: string, payload: Partial<WbPage> & { content?: any; page_code?: string; compiled_bundle?: string }) =>
    apiClient.put<WbPage>(`/wb/sites/${siteId}/pages/${pageId}`, payload),

  publish: (siteId: string, pageId: string) =>
    apiClient.post<WbPage>(`/wb/sites/${siteId}/pages/${pageId}/publish`, {}),

  unpublish: (siteId: string, pageId: string) =>
    apiClient.post<WbPage>(`/wb/sites/${siteId}/pages/${pageId}/unpublish`, {}),

  delete: (siteId: string, pageId: string) =>
    apiClient.delete<void>(`/wb/sites/${siteId}/pages/${pageId}`),
};

// ---------------------------------------------------------------------------
// Per-site AI chat persistence — /api/v1/wb/sites/:siteId/chat.
//
// The editor holds the chat in local state which is wiped on every page
// switch (the parent remounts AiCodeEditor with key={page.id}). Persisting
// the conversation per-site lets each remount reload the same chat → cross-
// page continuity. Both methods return the raw apiClient result (the
// documented swallow-error `{ data, error, success }` shape — apiClient never
// throws; callers branch on `res.error`, never try/catch).
// ---------------------------------------------------------------------------

export interface WbChatMessage {
  role: string;
  content: string;
}

export const wbSiteChatApi = {
  getChat: (siteId: string) =>
    apiClient.get<{ messages: WbChatMessage[] }>(`/wb/sites/${siteId}/chat`),

  saveChat: (siteId: string, messages: WbChatMessage[]) =>
    apiClient.put<{ messages: WbChatMessage[] }>(`/wb/sites/${siteId}/chat`, { messages }),
};

// ---------------------------------------------------------------------------
// Admin template catalog client — /api/v1/admin/wb-templates/*.
// Spec: docs/superpowers/specs/2026-05-26-website-builder-templates-design.md §14
//
// Mirrors the apiClient swallow-errors pattern documented in CLAUDE.md
// ("OrganizationAiContext"): every method returns `{ ok, data?, error?, code?,
// status? }` and never throws. UI branches on `res.ok`, never wraps these calls
// in try/catch (apiClient's outer try/catch already swallows errors into the
// result object — a UI try/catch would never fire).
// ---------------------------------------------------------------------------

export interface WbTemplatePageEntry {
  slug: string;
  title: string;
  is_home: boolean;
  source_s3_key: string;
}

export interface WbTemplateTokens {
  required: string[];
  optional: string[];
  defaults: Record<string, string>;
}

export interface WbTemplateAiRewrite {
  enabled: boolean;
  tone_hint: string;
  preserve_sections: string[];
}

/**
 * Nested API shape returned by `dbRowToApiShape` on the backend
 * ([WbTemplateAdminController.js](backend/src/interfaces/http/controllers/WbTemplateAdminController.js)).
 * Frontend ALWAYS works with this nested shape — never the flat DB columns
 * (`tokens_required`, `pages_manifest`, `ai_rewrite_tone_hint`, etc.).
 */
export interface WbTemplateAdminRow {
  id: string;
  name: string;
  category: string;
  business_types: string[];
  tagline: string;
  thumbnail_s3_key: string;
  thumbnail_url: string | null;
  page_count: number;
  features: string[];
  pages: WbTemplatePageEntry[];
  tokens: WbTemplateTokens;
  ai_rewrite: WbTemplateAiRewrite;
  version: number;
  is_active: boolean;
  created_by_user_id: string | null;
  created_by_name: string | null;
  /** Server may not populate these yet; treat as optional for forward-compat. */
  updated_by_user_id?: string | null;
  updated_by_name?: string | null;
  created_at: string;
  updated_at: string;
  duplicated_from_id?: string | null;
}

export interface WbTemplateAdminInput {
  id?: string; // omitted on update — id lives in the URL path
  name: string;
  category: string;
  business_types: string[];
  tagline: string;
  thumbnail_s3_key: string;
  features: string[];
  pages: WbTemplatePageEntry[];
  tokens: WbTemplateTokens;
  ai_rewrite: WbTemplateAiRewrite;
}

export interface WbTemplateValidateResult {
  ok: boolean;
  errors: string[];
}

export interface WbTemplateUploadResult {
  url: string;
  s3_key: string;
  size_bytes: number;
}

export interface WbAdminListParams {
  category?: string;
  status?: 'all' | 'draft' | 'published';
  q?: string;
}

// Swallow-error result discriminated union — matches the pattern in
// adminInvoices.ts / organization.ts / etc.
export type WbAdminOk<T> = { ok: true; data: T; error: null };
export type WbAdminErr = {
  ok: false;
  data: null;
  error: string;
  code?: string;
  status?: number;
  /** PUT-only — surfaced by the 412 stale handler so the UI can render
   *  "edited by ... — Reload / Overwrite anyway". */
  current_updated_at?: string | null;
};
export type WbAdminResult<T> = WbAdminOk<T> | WbAdminErr;

function wbAdminOk<T>(data: T): WbAdminOk<T> {
  return { ok: true, data, error: null };
}
function wbAdminFail(error: string, extra?: Partial<WbAdminErr>): WbAdminErr {
  return { ok: false, data: null, error, ...(extra || {}) };
}

/**
 * Normalise apiClient's `{ data, error, success }` response into the
 * `{ ok, data, error }` shape. apiClient's outer try/catch swallows errors
 * into `success: false` — we MUST inspect `error` / `success === false`
 * here (a UI-level try/catch around these calls would never fire).
 */
function wbAdminUnwrap<T>(response: any, fallbackError: string): WbAdminResult<T> {
  if (!response) return wbAdminFail(fallbackError);
  if (response.error || response.success === false) {
    return wbAdminFail(response.error || fallbackError);
  }
  // Backend sends `{ success: true, data: <T> }`. apiClient already unwraps
  // the outer envelope into `response.data` for us.
  const payload = (response.data ?? response) as T;
  return wbAdminOk<T>(payload);
}

export const wbAdminTemplatesApi = {
  async list(params?: WbAdminListParams): Promise<WbAdminResult<WbTemplateAdminRow[]>> {
    try {
      const res = await apiClient.get<WbTemplateAdminRow[]>('/admin/wb-templates', { params });
      return wbAdminUnwrap<WbTemplateAdminRow[]>(res, 'Failed to load templates');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to load templates');
    }
  },

  async get(id: string): Promise<WbAdminResult<WbTemplateAdminRow>> {
    try {
      const res = await apiClient.get<WbTemplateAdminRow>(`/admin/wb-templates/${id}`);
      return wbAdminUnwrap<WbTemplateAdminRow>(res, 'Failed to load template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to load template');
    }
  },

  async create(body: WbTemplateAdminInput): Promise<WbAdminResult<WbTemplateAdminRow>> {
    try {
      const res = await apiClient.post<WbTemplateAdminRow>('/admin/wb-templates', body);
      return wbAdminUnwrap<WbTemplateAdminRow>(res, 'Failed to create template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to create template');
    }
  },

  /**
   * Optimistic-concurrency PUT — server returns 412 + `current_updated_at`
   * if `If-Unmodified-Since` no longer matches. apiClient doesn't expose a
   * per-call headers option on PUT, so we fall back to fetch directly
   * (same Bearer-from-localStorage pattern as `wbAiApi.generateStream`
   * above and `downloadInvoicePdf` in adminInvoices.ts).
   */
  async update(
    id: string,
    body: WbTemplateAdminInput,
    ifUnmodifiedSince: string,
  ): Promise<WbAdminResult<WbTemplateAdminRow>> {
    let token = '';
    try {
      token = localStorage.getItem('auth_token') || '';
    } catch {
      // localStorage can throw in restricted iframes — proceed without
      // the header and let the backend 401 surface a clear error.
    }
    const url = `${API_CONFIG.BASE_URL}/admin/wb-templates/${id}`;
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'If-Unmodified-Since': ifUnmodifiedSince,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Network error');
    }
    let parsed: any = null;
    try {
      parsed = await res.json();
    } catch {
      parsed = null;
    }
    if (res.status === 412) {
      return wbAdminFail(
        parsed?.error || 'This template was edited by someone else.',
        {
          code: parsed?.code || 'TEMPLATE_STALE',
          status: 412,
          current_updated_at: parsed?.current_updated_at || null,
        },
      );
    }
    if (!res.ok) {
      return wbAdminFail(
        parsed?.error || parsed?.message || `HTTP ${res.status}`,
        { code: parsed?.code, status: res.status },
      );
    }
    if (parsed?.success === false) {
      return wbAdminFail(parsed?.error || 'Update failed', { code: parsed?.code });
    }
    return wbAdminOk<WbTemplateAdminRow>(parsed?.data ?? parsed);
  },

  async delete(id: string): Promise<WbAdminResult<{ success: true }>> {
    try {
      const res = await apiClient.delete<{ success: true }>(`/admin/wb-templates/${id}`);
      return wbAdminUnwrap<{ success: true }>(res, 'Failed to delete template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to delete template');
    }
  },

  async publish(id: string): Promise<WbAdminResult<WbTemplateAdminRow>> {
    try {
      const res = await apiClient.post<WbTemplateAdminRow>(
        `/admin/wb-templates/${id}/publish`,
        {},
      );
      return wbAdminUnwrap<WbTemplateAdminRow>(res, 'Failed to publish template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to publish template');
    }
  },

  async unpublish(id: string): Promise<WbAdminResult<WbTemplateAdminRow>> {
    try {
      const res = await apiClient.post<WbTemplateAdminRow>(
        `/admin/wb-templates/${id}/unpublish`,
        {},
      );
      return wbAdminUnwrap<WbTemplateAdminRow>(res, 'Failed to unpublish template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to unpublish template');
    }
  },

  async duplicate(id: string, newId: string): Promise<WbAdminResult<WbTemplateAdminRow>> {
    try {
      const res = await apiClient.post<WbTemplateAdminRow>(
        `/admin/wb-templates/${id}/duplicate`,
        { new_id: newId },
      );
      return wbAdminUnwrap<WbTemplateAdminRow>(res, 'Failed to duplicate template');
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to duplicate template');
    }
  },

  /**
   * Server returns `{ success: true, ok: boolean, errors: string[] }` — i.e.
   * the per-call HTTP envelope wraps the validate result. apiClient strips
   * the outer envelope into `response.data` but `data` here is the inner
   * `{ ok, errors }`. We expose that shape directly to callers.
   */
  async validate(id: string): Promise<WbAdminResult<WbTemplateValidateResult>> {
    try {
      const res = await apiClient.post<any>(`/admin/wb-templates/${id}/validate`, {});
      if (res?.error || res?.success === false) {
        return wbAdminFail(res?.error || 'Failed to validate template');
      }
      // The backend's reply.send({ success: true, ...result }) means the
      // ok/errors live at the top level. apiClient's envelope-unwrapper
      // promotes `data` when present; otherwise falls back to the whole
      // payload. Both shapes are tolerated here.
      const payload: any = res?.data ?? res;
      const result: WbTemplateValidateResult = {
        ok: payload?.ok === true,
        errors: Array.isArray(payload?.errors) ? payload.errors : [],
      };
      return wbAdminOk<WbTemplateValidateResult>(result);
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Failed to validate template');
    }
  },

  /**
   * Multipart upload — apiClient supports FormData bodies, but spec §14.7
   * shows the canonical fetch-direct pattern (Bearer token from
   * localStorage; same as `downloadInvoicePdf` / `sonnyChatUpload`). Fetch
   * gives us per-call headers + lets us surface non-JSON error responses
   * cleanly.
   */
  async uploadAsset(
    templateId: string,
    slot: 'thumbnail' | 'source' | 'gallery',
    file: File,
    pageSlug?: string,
  ): Promise<WbAdminResult<WbTemplateUploadResult>> {
    let token = '';
    try {
      token = localStorage.getItem('auth_token') || '';
    } catch {
      // ignore — see other branches for the rationale
    }
    const form = new FormData();
    form.append('type', slot);
    form.append('template_id', templateId);
    if (pageSlug) form.append('page_slug', pageSlug);
    form.append('file', file);

    const url = `${API_CONFIG.BASE_URL}/admin/wb-templates/upload-asset`;
    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
    } catch (err: any) {
      return wbAdminFail(err?.message || 'Network error');
    }
    let parsed: any = null;
    try {
      parsed = await res.json();
    } catch {
      parsed = null;
    }
    if (!res.ok) {
      return wbAdminFail(
        parsed?.error || parsed?.message || `Upload failed (HTTP ${res.status})`,
        { code: parsed?.code, status: res.status },
      );
    }
    if (parsed?.success === false) {
      return wbAdminFail(parsed?.error || 'Upload failed', { code: parsed?.code });
    }
    return wbAdminOk<WbTemplateUploadResult>({
      url: parsed?.url,
      s3_key: parsed?.s3_key,
      size_bytes: parsed?.size_bytes,
    });
  },
};

/**
 * Catalog category enum — kept in sync with the backend's validate body
 * (spec §14.4). Used by the admin UI dropdown.
 */
export const WB_TEMPLATE_CATEGORIES = [
  'restaurant',
  'retail',
  'salon',
  'fitness',
  'coaching',
  'clinic',
  'services',
  'real_estate',
  'creator',
  'landing',
] as const;
export type WbTemplateCategory = typeof WB_TEMPLATE_CATEGORIES[number];

/**
 * id regex — mirrored EXACTLY from the backend route's JSON schema in
 * [wbTemplatesRoutes.js](backend/src/interfaces/http/routes/admin/wbTemplatesRoutes.js).
 * Note: this differs from spec §14.4 (which said `{3,40}`). The route is
 * authoritative; we match it byte-for-byte. First char must be `[a-z0-9]`;
 * subsequent chars `[a-z0-9-]{0,63}`.
 */
export const WB_TEMPLATE_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;
